import { Fragment, useState } from 'react';
import { api } from '../api';
import { useLoad } from '../hooks';
import { metaText, stamp, timeAgo, untilNow } from '../format';
import {
  BudgetLine,
  Card,
  ConfirmButton,
  Empty,
  ErrorNote,
  Field,
  HealthBar,
  Loading,
  Modal,
  StatusPill,
  Table,
  Toggle,
} from '../components/ui';
import { ResourceDrawer } from '../components/ResourceDrawer';
import { AssistedLogin } from './AssistedLogin';
import { ImportAccounts } from './ImportAccounts';
import { NativeLogin } from './NativeLogin';
import type { IdentityTestResult, Page, ResourceSummary } from '../types';

/**
 * Kimlik havuzunun operatör görünümü.
 *
 * Parola ve oturum kimliği burada tam anlamıyla yalnızca-yazılır: girilebilir ve
 * değiştirilebilir, ama panelde onları geri okuyabilecek hiçbir yol yok. API yanıtlarının
 * onları koyabileceği bir alanı yok — yani bu bir arayüz alışkanlığı değil, yapısal bir
 * özellik. Metadata yalnızca oturumun VAR OLDUĞUNU söyler, değerini değil.
 */
/**
 * Oturumun nereden geldiği — hâlâ geçerli olup olmadığı değil, onu ancak bir test söyler.
 * Bir hesabın oturumu varsa Rab-bit parolayla giriş yapmaz, çünkü parolayla giriş bir
 * hesabı kaybetmenin en olası yoludur.
 */
/**
 * Bir testin sonucu, satırın altında.
 *
 * Kendi bileşeni çünkü satırın içindeyken yirmi satırlık iç içe koşul bloğuydu ve tablo
 * yapısını okunamaz hale getiriyordu. Söylediği şey değişmedi: kod tek başına teşhis
 * etmiyor, motorun döndürdüğü cümle de yanıtta zaten var.
 */
function TestLine({ test }: { test: IdentityTestResult | { error: string } }) {
  if ('error' in test) return <div className="test-line bad">{test.error}</div>;
  if (test.ok) {
    return (
      <div className="test-line good">
        {`başarılı · ${test.sessionAction ?? 'oturum tamam'}${
          test.externalIp ? ` · ${test.externalIp}` : ''
        }`}
      </div>
    );
  }
  return (
    <div className="test-line bad">
      {`başarısız · ${test.errorCode ?? 'bilinmiyor'}`}
      {test.errorMessage && <div className="test-why">{test.errorMessage}</div>}
      {test.errorCode === 'INSTAGRAM_CHALLENGE' && (
        /* Bu kod anlaşılır ama ÇÖZÜMÜ değil. Rab-bit challenge'ı bilerek çözmüyor,
           dolayısıyla Instagram'dan hiç kod istenmiyor ve operatör gelmeyen bir
           doğrulamayı bekliyor. Yapılacak şeyi burada yazmazsak hiçbir yerde yazmıyor. */
        <div className="test-why">
          Rab-bit doğrulama kodu istemez — challenge bilerek çözülmüyor. Bu hesaba{' '}
          <b>tarayıcıdan</b> gir, doğrulamayı orada tamamla ve tekrar Test'e bas. Mümkünse
          hesaba bağlı proxy'nin <b>ülkesinden</b> gir: oturumu bir ülkede kurup başka bir
          ülkeden kullanmak challenge'ı yeniden tetikler.
        </div>
      )}
      {test.errorCode === 'INSTAGRAM_SESSION_ALREADY_PRESENT' && (
        <div className="test-why">
          Bu hesabın kayıtlı bir oturumu var, o yüzden parolayla giriş denenmedi — parolayla
          giriş bir hesabı kaybetmenin en olası yolu. Kayıtlı oturumu denemek için Test'e bas.
        </div>
      )}
    </div>
  );
}

function sessionLabel(metadata: Record<string, unknown>): string {
  switch (metaText(metadata, 'sessionSource')) {
    case 'IMPORTED':
      return 'Var · yapıştırıldı';
    case 'NATIVE':
      return 'Var · girişle';
    case 'ASSISTED':
      return 'Var · tarayıcıdan';
    default:
      return 'Yok';
  }
}

export function InstagramAccounts() {
  const accounts = useLoad(() =>
    api.get<Page<ResourceSummary>>('/v1/resources?type=IDENTITY&limit=100'),
  );
  const proxies = useLoad(() =>
    api.get<Page<ResourceSummary>>('/v1/resources?type=PROXY&limit=100'),
  );

  const [creating, setCreating] = useState(false);
  const [importing, setImporting] = useState(false);
  const [editing, setEditing] = useState<ResourceSummary | null>(null);
  // Hangi satırların açık olduğu. Satır başına bir bayrak yerine tek küme: aynı anda
  // birden fazla hesabı yan yana karşılaştırmak isteyen operatör buna ihtiyaç duyuyor.
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [loggingIn, setLoggingIn] = useState<ResourceSummary | null>(null);
  const [nativeLogin, setNativeLogin] = useState<ResourceSummary | null>(null);
  const [inspecting, setInspecting] = useState<ResourceSummary | null>(null);
  const [tests, setTests] = useState<Record<string, IdentityTestResult | { error: string }>>({});
  const [testing, setTesting] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const toggleRow = (id: string) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      if (!next.delete(id)) next.add(id);
      return next;
    });

  const act = (work: Promise<unknown>) => {
    setError(null);
    work
      .then(() => accounts.reload())
      .catch((reason: unknown) =>
        setError(reason instanceof Error ? reason.message : String(reason)),
      );
  };

  const runTest = (account: ResourceSummary) => {
    setTesting(account.id);
    setError(null);
    api
      .post<IdentityTestResult>(`/v1/instagram/accounts/${account.id}/test`)
      .then((result) => setTests((prev) => ({ ...prev, [account.id]: result })))
      .catch((reason: unknown) =>
        setTests((prev) => ({
          ...prev,
          [account.id]: { error: reason instanceof Error ? reason.message : String(reason) },
        })),
      )
      .finally(() => {
        setTesting(null);
        accounts.reload();
      });
  };

  const rows = accounts.data?.items ?? [];
  const proxyOptions = (proxies.data?.items ?? []).filter((p) => !p.deactivatedAt);
  const proxyName = (id: string | null) =>
    id ? (proxyOptions.find((p) => p.id === id)?.name ?? id) : 'doğrudan';

  return (
    <>
      <Card
        title={`Instagram hesapları${rows.length ? ` (${rows.length})` : ''}`}
        flush
        actions={
          <>
            <button type="button" className="ghost" onClick={() => setImporting(true)}>
              Toplu ekle
            </button>
            <button type="button" onClick={() => setCreating(true)}>
              Hesap ekle
            </button>
          </>
        }
        footer="Yeni hesaplar kapalı başlar. Rotasyona girmeden önce Test et — doğrulanmamış bir kimlik havuza alınmaz."
      >
        {(error ?? accounts.error) && (
          <div style={{ padding: '14px 16px 0' }}>
            <ErrorNote message={error ?? accounts.error} />
          </div>
        )}
        {accounts.loading && rows.length === 0 && <Loading />}
        {!accounts.loading && rows.length === 0 && (
          <Empty title="Henüz hesap yok">
            Buradan ekleyebilir, ya da ortam değişkenindeki listeyi{' '}
            <code>pnpm identities:import-env</code> ile aktarabilirsin.
          </Empty>
        )}
        {rows.length > 0 && (
          <Table
            head={['', 'Hesap', 'Durum', 'Sağlık', 'Oturum', 'Proxy', 'Son başarı', '']}
          >
            {rows.map((account) => {
              const test = tests[account.id];
              const open = expanded.has(account.id);
              return (
                <Fragment key={account.id}>
                  <tr className={account.deactivatedAt ? 'row-retired' : undefined}>
                    <td className="row-toggle-cell">
                      <button
                        type="button"
                        className="row-toggle"
                        aria-expanded={open}
                        aria-label={`${account.name} ayrıntıları`}
                        onClick={() => toggleRow(account.id)}
                      >
                        {open ? '▾' : '▸'}
                      </button>
                    </td>
                    <td>
                      <button
                        type="button"
                        className="linky"
                        onClick={() => setInspecting(account)}
                      >
                        {account.name}
                      </button>
                      {/* API maskeli kopyayı ayrıca saklıyor, tam da bunun için. */}
                      <div className="mono muted small">
                        {metaText(account.metadata, 'usernameMasked') ?? '—'}
                      </div>
                      {test && <TestLine test={test} />}
                    </td>
                    <td>
                      <StatusPill status={account.status} />
                      {account.status === 'COOLDOWN' && (
                        <div className="muted small">
                          {untilNow(account.health.cooldownUntil)} kaldı
                        </div>
                      )}
                    </td>
                    <td>
                      <HealthBar score={account.health.healthScore} />
                      {account.health.consecutiveFailures > 0 && (
                        <div className="muted small">
                          arka arkaya {account.health.consecutiveFailures}
                        </div>
                      )}
                    </td>
                    <td className="muted small">{sessionLabel(account.metadata)}</td>
                    <td className="muted small">{proxyName(account.boundResourceId)}</td>
                    <td className="muted" title={stamp(account.health.lastSuccessAt)}>
                      {timeAgo(account.health.lastSuccessAt)}
                    </td>
                    {/* Satırda YALNIZCA iki eylem var. Beş buton bir hücreye sığmıyordu ve
                        tablo yatay kayıyordu; gerisi aşağıdaki açılır alanda, hepsi
                        etiketli. */}
                    <td className="actions">
                      <button
                        type="button"
                        className="tiny"
                        disabled={testing === account.id}
                        onClick={() => runTest(account)}
                      >
                        {testing === account.id ? 'Test ediliyor…' : 'Test'}
                      </button>
                      <button
                        type="button"
                        className="ghost tiny"
                        onClick={() => setEditing(account)}
                      >
                        Düzenle
                      </button>
                    </td>
                  </tr>
                  {open && (
                    <tr className="row-detail">
                      <td colSpan={8}>
                        <div className="detail">
                          <dl className="detail-facts">
                            <div>
                              <dt>Eşzamanlılık</dt>
                              <dd>en fazla {account.maxConcurrentLeases}</dd>
                            </div>
                            <div>
                              <dt>Bütçe</dt>
                              <dd>
                                <BudgetLine budget={account.budget} />
                              </dd>
                            </div>
                            <div>
                              <dt>Son hata</dt>
                              <dd className="muted">{account.health.lastErrorCode ?? '—'}</dd>
                            </div>
                          </dl>

                          <div className="detail-group">
                            <span className="detail-label">Proxy</span>
                            <select
                              value={account.boundResourceId ?? ''}
                              onChange={(e) =>
                                act(
                                  e.target.value
                                    ? api.post(
                                        `/v1/instagram/accounts/${account.id}/proxy/${e.target.value}`,
                                      )
                                    : api.post(
                                        `/v1/instagram/accounts/${account.id}/proxy/unassign`,
                                      ),
                                )
                              }
                            >
                              <option value="">doğrudan</option>
                              {proxyOptions.map((proxy) => (
                                <option key={proxy.id} value={proxy.id}>
                                  {proxy.name}
                                </option>
                              ))}
                            </select>
                          </div>

                          <div className="detail-group">
                            <span className="detail-label">Oturum kur</span>
                            {/* İkisi de bir oturum ÜRETİR. Hangisinin ne yaptığı buton
                                adından anlaşılmıyordu, o yüzden yanlarında yazıyor. */}
                            <button
                              type="button"
                              className="ghost tiny"
                              title="Parolayla giriş yapar ve doğrulama kodunu senden ister"
                              onClick={() => setNativeLogin(account)}
                            >
                              Parolayla giriş
                            </button>
                            <button
                              type="button"
                              className="ghost tiny"
                              title="Hesabın proxy'si üzerinden bir tarayıcı açar; girişi sen yaparsın"
                              onClick={() => setLoggingIn(account)}
                            >
                              Tarayıcıdan giriş
                            </button>
                          </div>

                          <div className="detail-group">
                            <span className="detail-label">Kullanımda</span>
                            <Toggle
                              on={
                                account.status !== 'DISABLED' && account.status !== 'CHALLENGED'
                              }
                              label={`${account.name} kullanımda`}
                              onChange={(next) =>
                                act(
                                  api.post(
                                    `/v1/resources/${account.id}/${next ? 'enable' : 'disable'}`,
                                  ),
                                )
                              }
                            />
                            <ConfirmButton
                              label="Emekli et"
                              confirmLabel="Emin misin?"
                              onConfirm={() =>
                                act(api.post(`/v1/resources/${account.id}/deactivate`))
                              }
                            />
                          </div>
                        </div>
                      </td>
                    </tr>
                  )}
                </Fragment>
              );
            })}
          </Table>
        )}
      </Card>

      {creating && (
        <CreateAccount
          proxies={proxyOptions}
          onClose={() => setCreating(false)}
          onDone={() => {
            setCreating(false);
            accounts.reload();
          }}
        />
      )}
      {importing && (
        <ImportAccounts
          onClose={() => setImporting(false)}
          onDone={() => accounts.reload()}
        />
      )}
      {nativeLogin && (
        <NativeLogin
          account={nativeLogin}
          onClose={() => setNativeLogin(null)}
          onDone={() => accounts.reload()}
        />
      )}
      {loggingIn && (
        <AssistedLogin
          account={loggingIn}
          onClose={() => setLoggingIn(null)}
          onDone={() => {
            setLoggingIn(null);
            accounts.reload();
          }}
        />
      )}
      {editing && (
        <EditAccount
          account={editing}
          proxies={proxyOptions}
          onClose={() => setEditing(null)}
          onDone={() => {
            setEditing(null);
            accounts.reload();
          }}
        />
      )}
      {inspecting && <ResourceDrawer resource={inspecting} onClose={() => setInspecting(null)} />}
    </>
  );
}

function CreateAccount({
  proxies,
  onClose,
  onDone,
}: {
  proxies: ResourceSummary[];
  onClose: () => void;
  onDone: () => void;
}) {
  const [name, setName] = useState('');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [sessionId, setSessionId] = useState('');
  const [totpSeed, setTotpSeed] = useState('');
  const [proxyResourceId, setProxyResourceId] = useState('');
  const [maxConcurrentLeases, setMax] = useState(1);
  const [maxLeasesPerHour, setPerHour] = useState('');
  const [maxLeasesPerDay, setPerDay] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = () => {
    setBusy(true);
    setError(null);
    const budget = {
      ...(maxLeasesPerHour ? { maxRunsPerHour: Number(maxLeasesPerHour) } : {}),
      ...(maxLeasesPerDay ? { maxRunsPerDay: Number(maxLeasesPerDay) } : {}),
    };
    api
      .post('/v1/instagram/accounts', {
        name,
        username,
        password,
        ...(sessionId.trim() ? { sessionId: sessionId.trim() } : {}),
        ...(totpSeed.trim() ? { totpSeed: totpSeed.trim().replace(/\s+/g, '') } : {}),
        maxConcurrentLeases,
        ...(proxyResourceId ? { proxyResourceId } : {}),
        ...(Object.keys(budget).length > 0 ? { budget } : {}),
      })
      .then(onDone)
      .catch((reason: unknown) =>
        setError(reason instanceof Error ? reason.message : String(reason)),
      )
      .finally(() => setBusy(false));
  };

  return (
    <Modal
      title="Instagram hesabı ekle"
      submitLabel="Oluştur"
      onClose={onClose}
      onSubmit={submit}
      busy={busy}
      error={error}
    >
      <Field label="Ad" hint="Bu hesaba her yerde bu isimle atıfta bulunulur.">
        <input value={name} onChange={(e) => setName(e.target.value)} required />
      </Field>
      <Field label="Instagram kullanıcı adı">
        <input
          value={username}
          autoComplete="off"
          onChange={(e) => setUsername(e.target.value)}
          required
        />
      </Field>
      <Field label="Parola" hint="Geldiği anda şifrelenir. Hiçbir yol onu geri okuyamaz.">
        <input
          type="password"
          value={password}
          autoComplete="new-password"
          onChange={(e) => setPassword(e.target.value)}
          required
        />
      </Field>
      <Field
        label="Oturum kimliği"
        hint="İsteğe bağlı ama şiddetle tavsiye edilir. Boş bırakırsan Rab-bit parolayla giriş yapmak zorunda kalır ve Instagram taze girişleri çoğunlukla doğrulamaya düşürür."
      >
        <input
          type="password"
          value={sessionId}
          autoComplete="off"
          placeholder="tarayıcıdaki sessionid çerezi"
          onChange={(e) => setSessionId(e.target.value)}
        />
      </Field>
      <p className="note">
        Oturumu almak için: bu hesaba aşağıda seçtiğin proxy üzerinden tarayıcıdan gir, geliştirici
        araçlarında Application &rarr; Cookies &rarr; instagram.com yolunu aç, <code>sessionid</code>{' '}
        çerezinin değerini kopyala. Girişi sen yaptığın için doğrulama adımı hiç oluşmuyor.
      </p>
      <Field
        label="Doğrulayıcı anahtarı"
        hint="Hesapta iki adımlı doğrulama bir uygulamayla yapılıyorsa (Google Authenticator gibi), kurulum sırasında verilen anahtarı buraya gir."
      >
        <input
          type="password"
          value={totpSeed}
          autoComplete="off"
          placeholder="örn. JBSWY3DPEHPK3PXP"
          onChange={(e) => setTotpSeed(e.target.value)}
        />
      </Field>
      <p className="note">
        Bu anahtar varsa sistem doğrulama kodunu kendisi üretir ve giriş için kimseye sormaz —
        oturum bir gün düşse bile kendi kendine geri döner. Yoksa kod her seferinde senden
        istenir. SMS ile gelen doğrulamada anahtar olmaz, orayı boş bırak.
      </p>
      <Field label="Proxy" hint="Çıkış adresi oturum boyunca sabit kalır.">
        <select value={proxyResourceId} onChange={(e) => setProxyResourceId(e.target.value)}>
          <option value="">doğrudan (proxy yok)</option>
          {proxies.map((proxy) => (
            <option key={proxy.id} value={proxy.id}>
              {proxy.name}
            </option>
          ))}
        </select>
      </Field>
      <Field label="Eşzamanlı çalışma" hint="Scraping hesabı için güvenli varsayılan: 1.">
        <input
          type="number"
          min={1}
          max={10}
          value={maxConcurrentLeases}
          onChange={(e) => setMax(Number(e.target.value))}
        />
      </Field>
      <div className="field-row">
        <Field label="Saatlik çalışma" hint="Boş bırakılırsa sınır yok.">
          <input
            type="number"
            min={1}
            value={maxLeasesPerHour}
            onChange={(e) => setPerHour(e.target.value)}
          />
        </Field>
        <Field label="Günlük çalışma">
          <input
            type="number"
            min={1}
            value={maxLeasesPerDay}
            onChange={(e) => setPerDay(e.target.value)}
          />
        </Field>
      </div>
      <p className="note">
        Yeni hesap kapalı olarak oluşturulur. Önce Test et, sonra aç — böylece doğrulanmamış bir
        kimlik rotasyona hiç girmez.
      </p>
    </Modal>
  );
}

/**
 * Bir hesapla ilgili değiştirilebilir her şey, tek yerde.
 *
 * Önceden yalnızca üç sırrı döndürüyordu ve gerisi satırdaki ayrı denetimlere dağılmıştı:
 * proxy bir açılır listede, eşzamanlılık hiçbir yerde, ad hiçbir yerde. Operatörün bir
 * hesabı düzenlemek için üç farklı yere tıklaması gerekiyordu ve bazı alanlar panelden
 * hiç değiştirilemiyordu.
 *
 * Sırlar burada da yalnızca-yazılır: girilebilir, ama panelde onları geri okuyabilecek bir
 * yol yok — API yanıtlarının onları koyabileceği bir alanı yok, yani bu bir arayüz
 * alışkanlığı değil, yapısal bir özellik. Boş bırakılan bir sır alanı saklanana dokunmaz.
 */
function EditAccount({
  account,
  proxies,
  onClose,
  onDone,
}: {
  account: ResourceSummary;
  proxies: ResourceSummary[];
  onClose: () => void;
  onDone: () => void;
}) {
  const [name, setName] = useState(account.name);
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [sessionId, setSessionId] = useState('');
  const [totpSeed, setTotpSeed] = useState('');
  const [concurrency, setConcurrency] = useState(String(account.maxConcurrentLeases));
  const [perHour, setPerHour] = useState(String(account.budget?.maxRunsPerHour ?? ''));
  const [perDay, setPerDay] = useState(String(account.budget?.maxRunsPerDay ?? ''));
  const [proxyId, setProxyId] = useState(account.boundResourceId ?? '');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = () => {
    setBusy(true);
    setError(null);

    const budget = {
      ...(perHour.trim() ? { maxRunsPerHour: Number(perHour) } : {}),
      ...(perDay.trim() ? { maxRunsPerDay: Number(perDay) } : {}),
    };

    // Yalnızca DEĞİŞEN alanlar gönderiliyor. Bir sır alanını boş bırakmak "sil" demek
    // değil, "dokunma" demek — oturum tazelerken parolayı sessizce silmek ya da tersi,
    // hesabı kurtarmaya çalışırken kaybetmenin yolu.
    const patch = {
      ...(name !== account.name ? { name } : {}),
      ...(username.trim() ? { username: username.trim().replace(/^@/, '') } : {}),
      ...(password ? { password } : {}),
      ...(sessionId.trim() ? { sessionId: sessionId.trim() } : {}),
      ...(totpSeed.trim() ? { totpSeed: totpSeed.trim().replace(/\s+/g, '') } : {}),
      ...(Number(concurrency) !== account.maxConcurrentLeases
        ? { maxConcurrentLeases: Number(concurrency) }
        : {}),
      ...(Object.keys(budget).length ? { budget } : {}),
    };

    const proxyChanged = proxyId !== (account.boundResourceId ?? '');

    const work = Object.keys(patch).length
      ? api.patch(`/v1/instagram/accounts/${account.id}`, patch)
      : Promise.resolve(null);

    work
      .then(() =>
        proxyChanged
          ? proxyId
            ? api.post(`/v1/instagram/accounts/${account.id}/proxy/${proxyId}`)
            : api.post(`/v1/instagram/accounts/${account.id}/proxy/unassign`)
          : null,
      )
      .then(onDone)
      .catch((reason: unknown) =>
        setError(reason instanceof Error ? reason.message : String(reason)),
      )
      .finally(() => setBusy(false));
  };

  return (
    <Modal
      title={`Düzenle — ${account.name}`}
      submitLabel="Kaydet"
      onClose={onClose}
      onSubmit={submit}
      busy={busy}
      error={error}
    >
      <Field label="Ad" hint="Yalnızca panelde görünen etiket. Instagram kullanıcı adı değil.">
        <input value={name} onChange={(e) => setName(e.target.value)} />
      </Field>
      <Field
        label="Instagram kullanıcı adı"
        hint={`Şu an: ${metaText(account.metadata, 'usernameMasked') ?? 'bilinmiyor'}. Boş bırakırsan değişmez.`}
      >
        <input
          value={username}
          autoComplete="off"
          placeholder="değiştirmek için yaz"
          onChange={(e) => setUsername(e.target.value)}
        />
      </Field>

      <Field
        label="Oturum kimliği"
        hint={`Şu an: ${sessionLabel(account.metadata)}. Boş bırakırsan saklanan oturum olduğu gibi kalır.`}
      >
        <input
          type="password"
          value={sessionId}
          autoComplete="off"
          placeholder="tarayıcıdaki sessionid çerezi"
          onChange={(e) => setSessionId(e.target.value)}
        />
      </Field>
      <Field
        label="Doğrulayıcı anahtarı"
        hint="İki adımlı doğrulama uygulamayla yapılıyorsa anahtarı gir; sistem kodu kendisi üretir. Boş bırakırsan değişmez."
      >
        <input
          type="password"
          value={totpSeed}
          autoComplete="off"
          placeholder="örn. JBSWY3DPEHPK3PXP"
          onChange={(e) => setTotpSeed(e.target.value)}
        />
      </Field>
      <Field label="Parola" hint="Boş bırakırsan saklanan parola olduğu gibi kalır.">
        <input
          type="password"
          value={password}
          autoComplete="new-password"
          onChange={(e) => setPassword(e.target.value)}
        />
      </Field>

      <Field label="Proxy" hint="Hesabın çıkışı. Çalışan bir hesabın proxy'sini değiştirmek challenge tetikleyebilir.">
        <select value={proxyId} onChange={(e) => setProxyId(e.target.value)}>
          <option value="">doğrudan</option>
          {proxies.map((proxy) => (
            <option key={proxy.id} value={proxy.id}>
              {proxy.name}
            </option>
          ))}
        </select>
      </Field>
      <Field
        label="Eşzamanlılık"
        hint="Bu hesabın aynı anda kaç çalıştırmaya verilebileceği. Bir kişi aynı anda iki telefondan gezmez; 1 dışında bir değer istisna olmalı."
      >
        <input
          type="number"
          min={1}
          max={10}
          value={concurrency}
          onChange={(e) => setConcurrency(e.target.value)}
        />
      </Field>
      <Field label="Saatlik çalıştırma tavanı" hint="Boş bırakırsan havuzun varsayılanı geçerli.">
        <input
          type="number"
          min={1}
          value={perHour}
          onChange={(e) => setPerHour(e.target.value)}
        />
      </Field>
      <Field label="Günlük çalıştırma tavanı" hint="Boş bırakırsan havuzun varsayılanı geçerli.">
        <input type="number" min={1} value={perDay} onChange={(e) => setPerDay(e.target.value)} />
      </Field>

      <p className="note">
        Oturum kimliği varsa Rab-bit parolayla giriş yapmaz. Instagram'ın doğrulama istediği yer
        taze giriştir; girişi tarayıcıda sen yaptığın için o adım hiç oluşmuyor. Oturum bir gün
        düşerse Rab-bit parolaya geri dönmez — hesabı yakacağı için bilerek durur ve senden yeni
        oturum ister.
      </p>
    </Modal>
  );
}
